// components/onboarding/AvaAssistant.jsx
// Enhanced AVA with AI Chat + Existing Tours + Tooltip Integration
// ================================================================

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTokens } from '../../context/TokenContext';
import { useNotification } from '../../context/NotificationContext';
import { 
  X, 
  ChevronRight, 
  ChevronLeft, 
  Gift, 
  HelpCircle, 
  Send, 
  MessageCircle,
  BookOpen,
  Loader,
  AlertCircle,
  ExternalLink
} from 'lucide-react';
import { awardTourCompletionBonus, BONUS_AMOUNTS } from '../../services/bonusService';
import * as avaService from '../../services/avaService';

const AvaAssistant = () => {
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'tours'
  const [currentTour, setCurrentTour] = useState(null);
  const [tourStep, setTourStep] = useState(0);
  const [hasSeenAva, setHasSeenAva] = useState(false);
  const [completedTours, setCompletedTours] = useState([]);
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [chatError, setChatError] = useState(null);
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Hooks
  const { user, getActiveUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshBalance } = useTokens();
  const { addNotification } = useNotification();
  
  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Check if new user and show AVA
  useEffect(() => {
    const activeUser = getActiveUser();
    if (!activeUser) return;
    
    const userType = activeUser.userType || localStorage.getItem('userType');
    const storageKey = userType === 'vendor' ? 'ava_vendor_seen' : 'ava_seen';
    const toursKey = userType === 'vendor' ? 'ava_vendor_tours_completed' : 'ava_tours_completed';
    
    const seen = localStorage.getItem(storageKey);
    const completed = JSON.parse(localStorage.getItem(toursKey) || '[]');
    setCompletedTours(completed);
    
    // Show Ava for new users
    if (!seen && activeUser) {
      setTimeout(() => {
        setIsOpen(true);
        setHasSeenAva(true);
        localStorage.setItem(storageKey, 'true');
        
        // Add welcome message
        setMessages([{
          role: 'assistant',
          content: `Hi ${activeUser.name?.split(' ')[0] || 'there'}! 👋 I'm AVA, your UniLedge assistant. I can answer questions about how things work, help you navigate the platform, or guide you through tours to earn tokens. What would you like to do?`
        }]);
        
      }, 2000);
    }
  }, [getActiveUser]);
  
  // ============================================
  // TOOLTIP INTEGRATION - Listen for "Ask AVA" clicks
  // ============================================
  useEffect(() => {
    const handleAvaOpen = async (event) => {
      const { prompt, context, autoSend } = event.detail || {};
    
      // Open AVA chat and switch to chat tab
      setIsOpen(true);
      setActiveTab('chat');
    
      // If prompt provided, set it and optionally auto-send
      if (prompt) {
        if (autoSend) {
          // For auto-send, we need to send directly since state updates are async
          // Add user message to chat
          const userMessage = prompt.trim();
          setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
          setIsTyping(true);
          setChatError(null);
          
          try {
            const response = await avaService.sendMessage({
              message: userMessage,
              conversationHistory: messages.slice(-10),
              currentPage: location.pathname
            });
            
            setIsTyping(false);
            
            if (response.success) {
              setMessages(prev => [...prev, { role: 'assistant', content: response.message }]);
            } else {
              setMessages(prev => [...prev, { role: 'assistant', content: response.message }]);
            }
          } catch (error) {
            setIsTyping(false);
            setChatError('Failed to send message. Please try again.');
            console.error('AVA tooltip chat error:', error);
          }
        } else {
          // Just populate the input field for user to review/send
          setInputValue(prompt);
        }
      }
    };
  
    window.addEventListener('ava-open', handleAvaOpen);
  
    return () => {
      window.removeEventListener('ava-open', handleAvaOpen);
    };
  }, [messages, location.pathname]); // Dependencies for the async handler
  
  // Tours configuration
  const tours = {
    // ← ADDED: Founding Member tour (first entry, available to all visitors including unauthenticated)
    foundingMember: {
      name: "Founding Member Tour",
      reward: 0, // No token reward — visitor hasn't signed up yet, tokens are earned at signup
      forVendors: false,
      forWaitlist: true, // ← Custom flag: show this tour even for unauthenticated visitors
      steps: [
        {
          message: "Hey! I'm AVA, your guide to UniLedge. We're building something special here in Sacramento — a community buying platform that saves you 25-40% on everyday essentials. Let me show you how it works! 🌟",
          action: "Let's Go!",
          target: null
        },
        {
          message: "UniLedge automatically groups you with neighbors in your area. The more people who join, the bigger the bulk order, and the lower the price for everyone. No memberships, no warehouse trips — just your neighborhood's combined buying power.",
          action: "Next",
          target: null
        },
        {
          message: "You earn tokens at every step of signup — 50 for joining, 25 for adding your phone, and 50 for completing your profile. That's up to 125 tokens ($12.50 in savings) before we even launch! Each token is worth $0.10 on your first order. 💰",
          action: "Show Me",
          target: null,
          highlight: '#signup-section'
        },
        {
          message: "After signup, you get a unique referral link. Every friend who joins earns you 25 tokens — with no cap. And here's the thing: referrals are one of the key signals we use to select Founding Members. More referrals = earlier access! 🔗",
          action: "Next",
          target: null
        },
        {
          message: "This isn't a random lottery. Founding Members are selected based on zip code density (we need enough neighbors for group buying to work) and your engagement (referrals, profile completion, participation). The more active you are, the sooner you'll get invited. 🏘️",
          action: "Next",
          target: null
        },
        {
          message: "Our launch catalog has 50 curated non-perishable essentials — rice, beans, pasta, cleaning supplies, personal care, coffee, and more. All sourced at true wholesale pricing, with savings of 20-40% off retail depending on group size. 🛒",
          action: "Next",
          target: null
        },
        {
          message: "Ready to get started? Complete all three signup tiers to max out your tokens, share your referral link with friends and family in Sacramento, and keep an eye on your email for Founding Member selection updates. I'm always here if you have questions! 🚀",
          action: "Got It!",
          target: null
        }
      ]
    },

    newMember: {
      name: "Welcome Tour",
      reward: BONUS_AMOUNTS.TOUR_WELCOME || 10,
      forVendors: false,
      steps: [
        {
          message: "Hi! I'm Ava, your UniLedge guide! Let me show you how to start saving and earning. 🎉",
          action: "Start Tour",
          target: null
        },
        {
          message: "First, complete your profile to boost your trust score by 0.2 points! Higher trust means better opportunities.",
          action: "Go to Profile",
          target: '/profile',
          highlight: '.profile-completion'
        },
        {
          message: "Next, verify a skill to unlock job opportunities. Even basic skills like organizing or driving count!",
          action: "View Skills",
          target: '/skills',
          highlight: '.add-skill-button'
        },
        {
          message: "Browse deals to start saving immediately. The more people buy, the lower the price! 💰",
          action: "Browse Deals",
          target: '/deals',
          highlight: '.product-card'
        },
        {
          message: "Great job! You've earned 10 tokens for completing the tour. Ready to save money and earn? 🚀",
          action: "Finish",
          target: null
        }
      ]
    },
    
    earner: {
      name: "Income Maximizer",
      reward: BONUS_AMOUNTS.TOUR_EARNER || 15,
      forVendors: false,
      steps: [
        {
          message: "Want to maximize your earnings? Let me show you the best opportunities! 💰",
          action: "Show Me",
          target: null
        },
        {
          message: "Check the Earn page for ways to earn tokens - from referrals to helping with deliveries!",
          action: "View Earn",
          target: '/earn',
          highlight: '.available-jobs'
        },
        {
          message: "Higher trust scores unlock better earning opportunities. Complete purchases to build trust!",
          action: "View Profile",
          target: '/profile',
          highlight: '.trust-score'
        },
        {
          message: "Share your referral link to earn 25 tokens when friends make their first purchase!",
          action: "View Referrals",
          target: '/earn',
          highlight: '.referral-section'
        },
        {
          message: "Awesome! You've earned 15 tokens. Now you're ready to maximize your income! 💪",
          action: "Finish",
          target: null
        }
      ]
    },

    tokenMastery: {
      name: "Token Mastery",
      reward: 20,
      forVendors: false,
      steps: [
        {
          message: "Let's master the token system! Tokens are UniLedge's reward currency. 10 tokens = $1.00 💰",
          action: "Got it!",
          target: null
        },
        {
          message: "You can redeem tokens at checkout for up to 40% off - but your tier sets a cap. New Users cap at $15, Platinum gets UNLIMITED! 💎",
          action: "View Wallet",
          target: '/wallet',
          highlight: '.token-balance'
        },
        {
          message: "Your tier is based on PURCHASES + REFERRALS, not just token balance. Shop more and refer friends to level up!",
          action: "See My Tier",
          target: '/wallet',
          highlight: '.loyalty-tier'
        },
        {
          message: "Tokens expire based on your tier - but ANY activity resets the clock! Just stay active and you're golden. ⏰",
          action: "Next",
          target: null
        },
        {
          message: "Pro tip: Platinum members (50+ purchases, 10+ referrals) get UNCAPPED 40% redemption AND tokens that NEVER expire! 🏆",
          action: "View Progress",
          target: '/wallet',
          highlight: '.tier-progress'
        },
        {
          message: "You've mastered tokens! Here's 20 tokens to practice with. Happy saving! 🎉",
          action: "Finish",
          target: null
        }
      ]
    },

    savingsExpert: {
      name: "Savings Expert",
      reward: 15,
      forVendors: false,
      steps: [
        {
          message: "Let's learn how UniLedge saves you money! It's not magic - it's math. 📊",
          action: "Show Me",
          target: null
        },
        {
          message: "When more people buy the same product, we negotiate better bulk rates. Those savings go directly to YOU!",
          action: "Browse Deals",
          target: '/deals',
          highlight: '.product-card'
        },
        {
          message: "Watch for price tiers: 5+ buyers = 10% off, 10+ = 18% off, 25+ = 27% off, 50+ = 38% off! 📉",
          action: "Next",
          target: null
        },
        {
          message: "Choose PICKUP to save even more! You'll skip the $5.99 delivery fee AND earn 10% bonus tokens (min 5, max 50). 🚗",
          action: "Go to Checkout",
          target: '/cart',
          highlight: '.checkout-button'
        },
        {
          message: "You're a Savings Expert now! Use these tips to maximize every purchase. +15 tokens earned! 💪",
          action: "Finish",
          target: null
        }
      ]
    },

    milestoneHunter: {
      name: "Milestone Hunter",
      reward: 20,
      forVendors: false,
      steps: [
        {
          message: "UniLedge has 55+ milestones with over 12,000 tokens available! Let's find them. 🏆",
          action: "Let's Hunt!",
          target: null
        },
        {
          message: "Milestones are grouped into categories: Shopping, Savings, Referrals, Jobs, Loyalty, Tokens, Trust, and Community.",
          action: "View Milestones",
          target: '/earn',
          highlight: '.milestones-tab'
        },
        {
          message: "Some are easy - like 'First Purchase' (25 tokens). Others are epic - like 'Savings Legend' for $10,000 saved (500 tokens)! 🎯",
          action: "Next",
          target: null
        },
        {
          message: "Check your progress anytime in Earn > Milestones. Click 'Check for New Achievements' to claim earned ones!",
          action: "Check Achievements",
          target: '/earn',
          highlight: '.check-achievements'
        },
        {
          message: "Pro tip: The Leaderboard rewards top achievers with monthly bonuses - 200 tokens for Top 10! 🥇",
          action: "View Leaderboard",
          target: '/earn',
          highlight: '.leaderboard-tab'
        },
        {
          message: "You're officially a Milestone Hunter! +20 tokens. Now go collect them all! 🎉",
          action: "Finish",
          target: null
        }
      ]
    },

    trustBuilder: {
      name: "Trust Builder",
      reward: 15,
      forVendors: false,
      steps: [
        {
          message: "Your Trust Score (0-5 stars) affects your earning potential. Let's build it up! ⭐",
          action: "Start",
          target: null
        },
        {
          message: "You start at 3.0. Every completed purchase, verified skill, and positive rating increases your score!",
          action: "View Profile",
          target: '/profile',
          highlight: '.trust-score'
        },
        {
          message: "Higher trust = better job opportunities. At 3.5+ you can become a neighborhood coordinator. At 4.0+ you unlock premium jobs!",
          action: "Next",
          target: null
        },
        {
          message: "Trust Badges show your reputation publicly. They help vendors and other members know you're reliable. 🛡️",
          action: "View Badges",
          target: '/profile',
          highlight: '.trust-badges'
        },
        {
          message: "You're on your way to becoming a trusted community member! +15 tokens for completing this tour. 🌟",
          action: "Finish",
          target: null
        }
      ]
    },
    
    platformTransparency: {
      name: "Platform Transparency",
      reward: 15,
      forVendors: false,
      steps: [
        {
          message: "UniLedge believes in complete transparency. Let me show you how we hold ourselves accountable! 🔍",
          action: "Show Me",
          target: null
        },
        {
          message: "Just like you have a Trust Score, UniLedge has one too! We're rated on delivery accuracy, order fulfillment, and member satisfaction.",
          action: "View Transparency",
          target: '/transparency',
          highlight: '.platform-trust-score'
        },
        {
          message: "Our Platform Trust Score updates in real-time based on YOUR feedback. Every rating you give matters! ⭐",
          action: "Next",
          target: null
        },
        {
          message: "Here you can see our commitments: transparent pricing, fair token system, responsive support, and community-first decisions.",
          action: "View Commitments",
          target: '/transparency',
          highlight: '.platform-commitments'
        },
        {
          message: "After every order, you can rate your experience. This feedback directly shapes our Platform Trust Score! 📊",
          action: "Next",
          target: null
        },
        {
          message: "Check the Home page anytime to see Platform Health metrics at a glance - we hide nothing! 💯",
          action: "View Home",
          target: '/',
          highlight: '.platform-health'
        },
        {
          message: "You've learned how UniLedge stays accountable! +15 tokens. Your feedback makes us better. 🎉",
          action: "Finish",
          target: null
        }
      ]
    },
    
    jobOpportunities: {
      name: "Job Opportunities",
      reward: 20,
      forVendors: false,
      steps: [
        {
          message: "Ready to earn? Let me show you how jobs work on UniLedge! 💼",
          action: "Let's Go!",
          target: null
        },
        {
          message: "Jobs are created automatically when orders come in. Delivery, sorting, coordination - there are 150+ job types!",
          action: "View Jobs",
          target: '/earn',
          highlight: '.jobs-tab'
        },
        {
          message: "Your Trust Score determines when you see jobs. At 4.5+ you get EXCLUSIVE access - a 2-hour head start! 🌟",
          action: "Next",
          target: null
        },
        {
          message: "Jobs are matched to YOUR area. You'll see nearby opportunities first, with distance shown in miles.",
          action: "Check Nearby",
          target: '/earn',
          highlight: '.nearby-jobs-button'
        },
        {
          message: "Pro tip: BUNDLE jobs together for bonus tokens! 2 jobs = +5% bonus, up to +20% for 5+ jobs! 📦",
          action: "Next",
          target: null
        },
        {
          message: "Look for these badges: 🌟 Exclusive (4.5+ trust), ⚡ Priority (3.5+ trust), 🚨 Urgent (deadline soon).",
          action: "Next",
          target: null
        },
        {
          message: "Watch for urgent jobs - they're available to everyone and need quick action! ⏰",
          action: "View Available",
          target: '/earn',
          highlight: '.available-jobs'
        },
        {
          message: "You're ready to start earning! +20 tokens. Build your trust score for better opportunities. 💪",
          action: "Finish",
          target: null
        }
      ]
    },
    
    vendorSuccess: {
      name: "Vendor Success Tour",
      reward: BONUS_AMOUNTS.TOUR_VENDOR || 20,
      forVendors: true,
      steps: [
        {
          message: "Welcome to your Vendor Dashboard! I'm Ava, and I'll help you succeed on UniLedge. 🚀",
          action: "Start Tour",
          target: null
        },
        {
          message: "This is your command center. Quick stats show your business health at a glance.",
          action: "Next",
          target: '/vendor/dashboard',
          highlight: '.overview-stats'
        },
        {
          message: "Add products here with bulk pricing. Groups love saving money!",
          action: "Products Tab",
          target: '/vendor/dashboard',
          highlight: '.products-tab'
        },
        {
          message: "Track orders from placement to fulfillment. Quick actions keep business flowing.",
          action: "Orders Tab",
          target: '/vendor/dashboard',
          highlight: '.orders-tab'
        },
        {
          message: "Excellent! You're ready to grow your business. You've earned 20 tokens! 🎉",
          action: "Finish",
          target: null
        }
      ]
    }
  };
  
  // Get available tours based on user type
  // ← MODIFIED: Waitlist visitors only see the Founding Member tour
  const getAvailableTours = () => {
    const activeUser = getActiveUser();
    const userType = activeUser?.userType || localStorage.getItem('userType');
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true' || 
                           localStorage.getItem('isLoggedIn') === 'true';
    
    // Unauthenticated waitlist visitors — only show Founding Member tour
    if (!isAuthenticated && !activeUser) {
      return Object.entries(tours).filter(([key, tour]) => tour.forWaitlist);
    }
    
    if (userType === 'vendor') {
      return Object.entries(tours).filter(([key, tour]) => tour.forVendors);
    }
    // Authenticated members — show all non-vendor tours (including Founding Member if they want to retake)
    return Object.entries(tours).filter(([key, tour]) => !tour.forVendors);
  };
  
  // Handle sending chat message
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isTyping) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    setChatError(null);
    
    // Add user message to chat
    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    
    // Show typing indicator
    setIsTyping(true);
    
    try {
      // Call AVA service
      const response = await avaService.sendMessage({
        message: userMessage,
        conversationHistory: newMessages.slice(-10), // Last 10 messages for context
        currentPage: location.pathname
      });
      
      setIsTyping(false);
      
      if (response.success) {
        setMessages(prev => [...prev, { role: 'assistant', content: response.message }]);
        
        // Handle escalation suggestion
        if (response.shouldEscalate) {
          // Could show escalation button here
        }
      } else {
        // Show error message from AVA
        setMessages(prev => [...prev, { role: 'assistant', content: response.message }]);
      }
      
    } catch (error) {
      setIsTyping(false);
      setChatError('Failed to send message. Please try again.');
      console.error('Chat error:', error);
    }
  };
  
  // Handle key press in input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  // Handle tour step action
  const handleStepAction = () => {
    const step = tours[currentTour].steps[tourStep];
    
    if (step.target) {
      navigate(step.target);
      
      setTimeout(() => {
        const element = document.querySelector(step.highlight);
        if (element) {
          element.classList.add('ava-highlight');
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          
          setTimeout(() => {
            element.classList.remove('ava-highlight');
          }, 3000);
        }
      }, 500);
    }
    
    if (tourStep < tours[currentTour].steps.length - 1) {
      setTourStep(tourStep + 1);
    } else {
      completeTour();
    }
  };
  
  // Complete tour and award tokens
  const completeTour = async () => {
    const tour = tours[currentTour];
    const reward = tour.reward;
    const activeUser = getActiveUser();
    const userType = activeUser?.userType || localStorage.getItem('userType');
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true' || 
                           localStorage.getItem('isLoggedIn') === 'true';
    
    // ← ADDED: Skip bonus API call for unauthenticated visitors (Founding Member tour has reward: 0)
    if (isAuthenticated && activeUser && reward > 0) {
      try {
        const result = await awardTourCompletionBonus(currentTour);
        
        if (result.success) {
          if (refreshBalance) refreshBalance();
          
          addNotification?.({
            type: 'success',
            title: 'Tour Complete! 🎉',
            message: `You earned ${reward} tokens for completing the ${tour.name}!`,
            showToast: true
          });
        } else if (result.alreadyClaimed) {
          addNotification?.({
            type: 'info',
            title: 'Tour Already Completed',
            message: `You've already earned tokens for this tour.`,
            showToast: true
          });
        }
      } catch (error) {
        console.error('Error awarding tour tokens:', error);
      }
    } else if (!isAuthenticated) {
      // Waitlist visitor completed the Founding Member tour — no tokens, just a friendly nudge
      addNotification?.({
        type: 'success',
        title: 'Tour Complete! 🌟',
        message: `Now sign up as a Founding Member to start earning tokens!`,
        showToast: true
      });
    }
    
    const updatedCompleted = [...completedTours, currentTour];
    setCompletedTours(updatedCompleted);
    
    const toursKey = userType === 'vendor' ? 'ava_vendor_tours_completed' : 'ava_tours_completed';
    localStorage.setItem(toursKey, JSON.stringify(updatedCompleted));
    
    setCurrentTour(null);
    setTourStep(0);
    setActiveTab('chat');
  };
  
  // Start a tour
  const startTour = (tourName) => {
    setCurrentTour(tourName);
    setTourStep(0);
    setActiveTab('tours');
  };
  
  // Skip tour
  const skipTour = () => {
    setCurrentTour(null);
    setTourStep(0);
    setActiveTab('chat');
  };
  
  // Quick action buttons — swap for waitlist visitors vs. authenticated users
  // ← MODIFIED: Conditional based on current page
  const isWaitlistPage = location.pathname === '/' && !(
    localStorage.getItem('isAuthenticated') === 'true' || 
    localStorage.getItem('isLoggedIn') === 'true'
  );

  const quickActions = isWaitlistPage
    ? [
        { label: '🏘️ What is UniLedge?',     message: 'What is UniLedge and how does it work?' },
        { label: '🪙 How do I earn tokens?',  message: 'How do I earn tokens on the waitlist?' },
        { label: '📅 When do you launch?',     message: 'When will UniLedge launch in my area?' },
        { label: '🔗 How do referrals work?',  message: 'How does the referral program work?' },
      ]
    : [
        { label: '🛒 How to buy', message: 'How do I make a purchase?' },
        { label: '🪙 My tokens', message: 'How do I check my token balance?' },
        { label: '📦 Track order', message: 'How do I track my order?' },
        { label: '🎁 Referrals', message: 'How do referrals work?' },
      ];
  
  // Don't show on certain pages
  const hiddenPages = ['/login', '/register', '/vendor/login', '/vendor/register',  '/quick-intro'/*, '/welcome'*/];
  if (hiddenPages.includes(location.pathname)) {
    return null;
  }
  
  // Floating button when closed
  if (!isOpen) {
    const availableTours = getAvailableTours();
    const hasIncompleteTours = availableTours.some(([key]) => !completedTours.includes(key));
    
    return (
      <button 
        className="fixed bottom-20 right-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 z-40"
        onClick={() => setIsOpen(true)}
        aria-label="Open Ava Assistant"
      >
        <div className="relative">
          <MessageCircle size={24} />
          {hasIncompleteTours && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-pulse" />
          )}
        </div>
      </button>
    );
  }
  
  const activeUser = getActiveUser();
  const availableTours = getAvailableTours();
  
  return (
    <div className="fixed bottom-20 right-4 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl z-50 overflow-hidden flex flex-col" style={{ maxHeight: '70vh' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-3">
              <img 
                src="https://res.cloudinary.com/drcldizc0/image/upload/v1766806358/lucid-origin_Professional_female_customer_service_representative_with_a_modern_wireless_heads-0_ravzgs.jpg" 
                alt="AVA"
                className="w-full h-full object-cover rounded-full"
              />
            </div>
            <div>
              <h3 className="font-semibold">AVA</h3>
              <p className="text-xs opacity-90">Your UniLedge Assistant</p>
            </div>
          </div>
          <button 
            className="text-white hover:bg-white/20 rounded-lg p-1 transition-colors"
            onClick={() => setIsOpen(false)}
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Tabs */}
        {!currentTour && (
          <div className="flex mt-3 bg-white/20 rounded-lg p-1">
            <button
              className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center ${
                activeTab === 'chat' ? 'bg-white text-blue-600' : 'text-white'
              }`}
              onClick={() => setActiveTab('chat')}
            >
              <MessageCircle size={14} className="mr-1" />
              Chat
            </button>
            <button
              className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center ${
                activeTab === 'tours' ? 'bg-white text-blue-600' : 'text-white'
              }`}
              onClick={() => setActiveTab('tours')}
            >
              <BookOpen size={14} className="mr-1" />
              Tours
              {availableTours.some(([key]) => !completedTours.includes(key)) && (
                <span className="ml-1 w-2 h-2 bg-yellow-400 rounded-full" />
              )}
            </button>
          </div>
        )}
      </div>
      
      {/* Content Area */}
      <div className="flex-1 overflow-y-auto">
        {/* Chat Tab */}
        {activeTab === 'chat' && !currentTour && (
          <div className="flex flex-col h-full">
            {/* Messages */}
            <div className="flex-1 p-4 space-y-3 overflow-y-auto" style={{ minHeight: '200px', maxHeight: '300px' }}>
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 py-4">
                  <MessageCircle size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Ask me anything about UniLedge!</p>
                </div>
              ) : (
                messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                        msg.role === 'user'
                          ? 'bg-blue-500 text-white rounded-br-md'
                          : 'bg-gray-100 text-gray-800 rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))
              )}
              
              {/* Typing indicator */}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
            
            {/* Quick Actions */}
            {messages.length === 0 && (
              <div className="px-4 pb-2">
                <p className="text-xs text-gray-500 mb-2">Quick questions:</p>
                <div className="flex flex-wrap gap-1">
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded-full transition-colors"
                      onClick={() => {
                        setInputValue(action.message);
                        setTimeout(() => handleSendMessage(), 100);
                      }}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Error message */}
            {chatError && (
              <div className="px-4 py-2 bg-red-50 text-red-600 text-sm flex items-center">
                <AlertCircle size={14} className="mr-2" />
                {chatError}
              </div>
            )}
            
            {/* Input */}
            <div className="p-3 border-t bg-gray-50">
              <div className="flex items-center space-x-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask me anything..."
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-full text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                  disabled={isTyping}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isTyping}
                  className={`p-2 rounded-full transition-colors ${
                    inputValue.trim() && !isTyping
                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                      : 'bg-gray-200 text-gray-400'
                  }`}
                >
                  {isTyping ? <Loader size={18} className="animate-spin" /> : <Send size={18} />}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Tours Tab */}
        {activeTab === 'tours' && !currentTour && (
          <div className="p-4 space-y-3">
            <p className="text-sm text-gray-600">
              Complete tours to learn the platform and earn tokens! 🎁
            </p>
            
            {availableTours.map(([key, tour]) => {
              const isCompleted = completedTours.includes(key);
              
              return (
                <button
                  key={key}
                  onClick={() => startTour(key)}
                  disabled={isCompleted}
                  className={`w-full p-3 rounded-lg text-left transition-colors ${
                    isCompleted 
                      ? 'bg-green-50 hover:bg-green-100 text-green-700 border border-green-200'
                      : 'bg-blue-50 hover:bg-blue-100 text-blue-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium flex items-center">
                        {tour.name}
                        {isCompleted && <span className="ml-2 text-green-600">✓</span>}
                      </h4>
                      <p className="text-sm opacity-80">
                        {isCompleted ? 'Completed ✓ (tap to retake)' : `Earn ${tour.reward} tokens`}
                      </p>
                    </div>
                    {!isCompleted && <Gift size={20} className="text-yellow-500" />}
                  </div>
                </button>
              );
            })}
            
            {availableTours.every(([key]) => completedTours.includes(key)) && (
              <div className="text-center py-4 text-gray-500">
                <p className="text-sm">🎉 All tours completed!</p>
                <p className="text-xs mt-1">Check back for new tours.</p>
              </div>
            )}
          </div>
        )}
        
        {/* Active Tour */}
        {currentTour && (
          <div className="p-4 space-y-4">
            {/* Progress */}
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>Step {tourStep + 1} of {tours[currentTour].steps.length}</span>
              <button 
                onClick={skipTour}
                className="text-gray-500 hover:text-gray-700"
              >
                Skip tour
              </button>
            </div>
            
            <div className="bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-blue-500 to-purple-600 h-full rounded-full transition-all duration-300"
                style={{ 
                  width: `${((tourStep + 1) / tours[currentTour].steps.length) * 100}%` 
                }}
              />
            </div>
            
            {/* Step Content */}
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-gray-800">
                {tours[currentTour].steps[tourStep].message}
              </p>
            </div>
            
            {/* Actions */}
            <div className="flex justify-between">
              {tourStep > 0 && (
                <button 
                  onClick={() => setTourStep(tourStep - 1)}
                  className="flex items-center text-gray-600 hover:text-gray-800"
                >
                  <ChevronLeft size={16} className="mr-1" />
                  Back
                </button>
              )}
              
              <button 
                onClick={handleStepAction}
                className="ml-auto bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:shadow-lg transition-all flex items-center"
              >
                {tours[currentTour].steps[tourStep].action}
                <ChevronRight size={16} className="ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      {!currentTour && (
        <div className="bg-gray-50 px-4 py-2 text-center border-t flex-shrink-0">
          <p className="text-xs text-gray-500">
            {completedTours.length} of {availableTours.length} tours completed
          </p>
        </div>
      )}
    </div>
  );
};

// CSS for highlighting (inject once)
if (typeof document !== 'undefined') {
  const styleId = 'ava-highlight-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .ava-highlight {
        position: relative;
        box-shadow: 0 0 0 3px #3B82F6, 0 0 0 6px rgba(59, 130, 246, 0.3) !important;
        animation: ava-pulse 2s infinite;
        z-index: 1000;
      }
      
      @keyframes ava-pulse {
        0% { box-shadow: 0 0 0 3px #3B82F6, 0 0 0 6px rgba(59, 130, 246, 0.3); }
        50% { box-shadow: 0 0 0 3px #3B82F6, 0 0 0 12px rgba(59, 130, 246, 0.1); }
        100% { box-shadow: 0 0 0 3px #3B82F6, 0 0 0 6px rgba(59, 130, 246, 0.3); }
      }
    `;
    document.head.appendChild(style);
  }
}

export default AvaAssistant;